import type { LandingPageContent } from './types'

// GitLab-Issue #144 (Sub-Issue von #142): Schritt-für-Schritt-How-to-Seite für Handwerksbetriebe.
// Nutzt dieselbe HowToStep-Infrastruktur wie so-funktioniert-es-fuer-kunden.ts (#143). Jeder
// Schritt wurde live gegen die QA-Docker-Umgebung nachvollzogen (frischer Handwerker-Test-Account,
// echtes Angebot auf Anfrage #30) - siehe QA-Bericht im Issue-Kommentar.
export const SO_FUNKTIONIERT_ES_FUER_HANDWERKER: LandingPageContent = {
  slug: 'so-funktioniert-es-fuer-handwerker',
  lang: 'de',
  topicId: 'so-funktioniert-es-fuer-handwerker',
  title: 'So funktioniert Roofora für Handwerksbetriebe – Anleitung Schritt für Schritt',
  metaDescription:
    'Schritt-für-Schritt-Anleitung für Handwerksbetriebe: Account erstellen, anonymisierte ' +
    'Solaranfragen einsehen, kostenlos ein Angebot abgeben und Kontakt zum Kunden anfragen - mit ' +
    'echten Screenshots aus der Anwendung.',
  keywords: [
    'Roofora Handwerker Anleitung',
    'Roofora für Handwerksbetriebe wie funktioniert',
    'Solaranfragen einsehen Handwerker',
    'Photovoltaik Angebot abgeben Anleitung',
    'Kontakt zum Kunden anfragen Solaranlage',
  ],
  eyebrow: 'Anleitung für Handwerksbetriebe',
  h1: 'So funktioniert Roofora für Handwerksbetriebe',
  intro:
    'Diese Anleitung zeigt jeden Schritt aus Sicht eines Handwerksbetriebs - von der Registrierung ' +
    'über die anonymisierte Anfragenliste bis zur Angebotsabgabe und Kontaktanfrage - mit echten ' +
    'Screenshots aus der Anwendung.',
  sections: [
    { type: 'heading', text: 'In 3 Minuten erklärt' },
    {
      type: 'paragraph',
      text:
        'Sie registrieren einen kostenlosen Firmen-Account und sehen anschließend anonymisierte ' +
        'Solaranfragen aus Ihrer Region - mit Dachdaten, Verbrauch und Fotos, aber ohne ' +
        'Kontaktdaten des Kunden. Sie geben kostenlos und unverbindlich ein Angebot ab und fragen ' +
        'gleichzeitig Kontakt an. Erst wenn der Kunde genau Ihren Betrieb individuell freigibt, ' +
        'erhalten Sie E-Mail und/oder Telefonnummer.',
    },
    { type: 'heading', text: 'Was brauche ich vorher?' },
    {
      type: 'list',
      items: [
        'Einen Firmennamen',
        'Keine E-Mail-Adresse und keine Telefonnummer nötig, nur einen selbst gewählten Username und ein Passwort',
        'Für ein aussagekräftiges Angebot: einen ungefähren Preis und optional ein PDF-Angebot',
      ],
    },
  ],
  steps: [
    {
      title: 'Einstieg und Registrierung',
      whatYouSee:
        'Auf der Handwerker-Registrierungsseite ein Formular mit Firmenname, Username (mit Link ' +
        '"Zufälligen Username generieren") und Passwort, dazu eine Kurzübersicht "So funktioniert\'s": ' +
        'Anfragen einsehen, Angebot abgeben, Kontakt anfragen.',
      whatToDo:
        'Klicken Sie im Hero-Bereich der Startseite auf "Als Handwerker Aufträge finden", tragen Sie ' +
        'Ihren Firmennamen ein, wählen Sie einen Username und ein Passwort mit mindestens 8 Zeichen ' +
        'und schließen Sie die Registrierung ab.',
      whatHappensNext:
        'Wie beim Kunden-Account erhalten Sie einen Recovery-Code - es gibt keinen Passwort-Reset ' +
        'per E-Mail, bewahren Sie ihn deshalb sicher auf.',
      successCriterion: 'Sie gelangen in Ihr Handwerker-Dashboard.',
      screenshot: {
        src: '/how-to/handwerker/01-registrierung.jpg',
        alt: 'Handwerker-Registrierungsformular mit Firmenname, Username und Passwort',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Dashboard-Übersicht',
      whatYouSee:
        'Ihren Firmennamen, die Handwerker-ID sowie vier Kacheln - "Offene Anfragen" (mit Anzahl ' +
        'neuer Anfragen seit dem letzten Login), "Meine Kontaktanfragen", "Merkliste" und ' +
        '"B2B-Marktplatz".',
      whatToDo: 'Klicken Sie auf "Zu den offenen Anfragen".',
      whatHappensNext: 'Die anonymisierte Liste aller offenen Solaranfragen in Deutschland öffnet sich.',
      successCriterion: 'Die Übersicht "Offene Anfragen (anonymisiert)" ist sichtbar.',
      screenshot: {
        src: '/how-to/handwerker/02-dashboard.jpg',
        alt: 'Handwerker-Dashboard mit den Kacheln Offene Anfragen, Meine Kontaktanfragen, Merkliste, B2B-Marktplatz',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Offene Anfragen durchsuchen',
      whatYouSee:
        'Eine Tabelle mit anonymisierten Anfragen - Auftragstyp, PLZ, Erstellungs- und Ablaufdatum, ' +
        'Anlagenkennwerte, Anzahl bereits abgegebener Angebote und Anzahl Freigaben. Darüber Filter ' +
        'nach Auftragstyp, Land, PLZ und Anfrage-ID.',
      whatToDo:
        'Filtern Sie nach PLZ oder Auftragstyp, um passende Anfragen aus Ihrer Region zu finden, und ' +
        'klicken Sie auf den Pfeil einer Zeile, um sie aufzuklappen.',
      whatHappensNext:
        'Sie sehen ausschließlich die Anzahl bereits abgegebener Angebote, nicht deren Preise - ' +
        'Handwerksbetriebe bieten unabhängig voneinander.',
      successCriterion: 'Eine oder mehrere passende Anfragen sind in der Liste sichtbar.',
      screenshot: {
        src: '/how-to/handwerker/03-offene-anfragen-liste.jpg',
        alt: 'Tabelle offener, anonymisierter Anfragen mit Filtern nach PLZ und Auftragstyp',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Anfrage-Details prüfen',
      whatYouSee:
        'Nach dem Aufklappen alle relevanten Angaben als Badges - Gebäudetyp, Dachflächen, Dachtyp ' +
        'und -material, Baujahr, Kabelführung, Montageort für Wechselrichter/Speicher, Personen im ' +
        'Haushalt, Verbrauch, Batteriepräferenz, Kontaktwunsch mit Zeitfenster - sowie die ' +
        'hochgeladenen Dach- und Zählerschrank-Fotos. Keine Kontaktdaten des Kunden.',
      whatToDo:
        'Prüfen Sie, ob die Anfrage zu Ihrem Betrieb passt (Region, Auftragstyp, Dachzustand auf den Fotos).',
      whatHappensNext: 'Passt die Anfrage, scrollen Sie zum Angebotsformular direkt unterhalb der Details.',
      successCriterion: 'Sie haben genug Informationen, um einen realistischen Preis zu schätzen.',
      screenshot: {
        src: '/how-to/handwerker/04-anfrage-detail.jpg',
        alt: 'Aufgeklappte Anfrage-Details mit Badges zu Gebäude, Dach, Verbrauch und hochgeladenen Fotos',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Angebot abgeben und Kontakt anfragen',
      whatYouSee:
        'Ein Feld für den geschätzten Preis, eine optionale Herstellerauswahl, ein Freitextfeld ' +
        '"Warum sollten Kunden Sie wählen?" und die Möglichkeit, ein PDF-Angebot hochzuladen, dazu ' +
        'der Button "Angebot abgeben & Kontakt anfragen".',
      whatToDo:
        'Tragen Sie einen geschätzten Preis ein, ergänzen Sie optional ein kurzes ' +
        'Alleinstellungsmerkmal Ihres Betriebs und klicken Sie auf "Angebot abgeben & Kontakt anfragen".',
      whatHappensNext:
        'Ihr Angebot und Ihre Kontaktanfrage werden in einem Schritt übermittelt - beides ist für ' +
        'Sie kostenlos und unverbindlich. Der Kunde entscheidet individuell, ob er Ihnen seine ' +
        'Kontaktdaten freigibt.',
      successCriterion: 'Eine grüne Bestätigung "Angebot gesendet ✓" erscheint.',
      troubleshooting:
        'Tragen Sie im Freitextfeld keine eigenen Kontaktdaten (E-Mail, Telefonnummer) ein - das ' +
        'führt zu einer Fehlermeldung. Angebote mit PDF-Dokument werden nach Erfahrung der Plattform häufiger freigegeben.',
      screenshot: {
        src: '/how-to/handwerker/05-angebot-formular.jpg',
        alt: 'Ausgefülltes Angebotsformular mit Preis und Begründungstext, Button Angebot abgeben & Kontakt anfragen',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Bestätigung und weiteres Vorgehen',
      whatYouSee: 'Die grüne Meldung "Angebot gesendet ✓" oberhalb der (jetzt leeren) Anfragenliste.',
      whatToDo:
        'Warten Sie auf die Entscheidung des Kunden - unter "Meine Kontaktanfragen" im Dashboard ' +
        'sehen Sie den Status aller gestellten Anfragen (gestellt, angenommen, freigegeben ohne PDF-Angebot).',
      whatHappensNext:
        'Gibt der Kunde Ihren Betrieb individuell frei, erhalten Sie dort E-Mail und/oder ' +
        'Telefonnummer sowie das bevorzugte Zeitfenster für die Kontaktaufnahme.',
      successCriterion: 'Das Angebot erscheint unter "Meine Kontaktanfragen" mit dem Status "Gestellt".',
      troubleshooting:
        'Ohne hinterlegte E-Mail-Adresse werden Sie nicht aktiv benachrichtigt, wenn der Kunde ' +
        'freigibt - schauen Sie deshalb regelmäßig in Ihrem Account vorbei.',
      screenshot: {
        src: '/how-to/handwerker/06-angebot-gesendet.jpg',
        alt: 'Bestätigungsmeldung "Angebot gesendet" in der Anfragenliste',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: 'Kostet die Nutzung als Handwerksbetrieb etwas?',
      a:
        'Nein. Registrierung, Anfragen einsehen, Angebote abgeben und Kontakt anfragen sind aktuell ' +
        'für Handwerksbetriebe kostenlos.',
    },
    {
      q: 'Sehe ich die Preise anderer Handwerksbetriebe?',
      a:
        'Nein. Sie sehen nur die Anzahl bereits abgegebener Angebote, nicht deren Höhe - jeder ' +
        'Betrieb bietet unabhängig voneinander.',
    },
    {
      q: 'Bekomme ich automatisch Kontaktdaten, wenn ich ein Angebot abgebe?',
      a:
        'Nein. Sie fragen mit dem Angebot lediglich Kontakt an. Kontaktdaten erhalten Sie erst, wenn ' +
        'der Kunde genau Ihren Betrieb individuell freigibt.',
    },
    {
      q: 'Kann ich mein Angebot nachträglich ändern?',
      a: 'Ja, solange die Anfrage noch offen ist, können Sie Ihr Angebot jederzeit anpassen oder nachbessern.',
    },
    {
      q: 'Was, wenn der Kunde meine Kontaktanfrage nicht freigibt?',
      a:
        'Es entstehen keine Kosten und keine Verpflichtung - Sie können weiterhin andere passende ' +
        'Anfragen in Ihrer Region durchsuchen.',
    },
  ],
  primaryCta: { label: 'Jetzt kostenlos als Handwerksbetrieb registrieren', action: 'craftsman' },
  secondaryCta: { label: 'Anleitung für Kunden ansehen', action: 'link', href: '/so-funktioniert-es-fuer-kunden' },
  relatedLinks: [
    { label: 'Für Handwerksbetriebe: mehr Aufträge finden', href: '/handwerker-photovoltaik-auftraege' },
    { label: 'So funktioniert Roofora für Kunden', href: '/so-funktioniert-es-fuer-kunden' },
    // GitLab-Issue #145 (Schritt 5): Cross-Link zur B2B-Marktplatz-Anleitung - die "B2B-Marktplatz"-
    // Dashboard-Kachel wird in Schritt 2 oben bereits erwähnt.
    { label: 'So funktioniert der B2B-Marktplatz', href: '/so-funktioniert-es-fuer-b2b-marktplatz' },
  ],
}
